import clsx from "clsx";
import { t } from "i18next";
import { useCallback, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { assignOrderToInvoice } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { Order } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	allPhasesData: ProjectPhase[];
	allAvailableOrders: Order[];
	invoiceData: Invoice;
	onCancel: () => void;
	onSuccess: () => void;
};

const AssignProcessedInvoiceToOrderForm = ({
	allPhasesData,
	allAvailableOrders,
	onCancel,
	onSuccess,
	invoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [selectedOrderId, setSelectedOrderId] = useState<string | null>();

	const handleSubmit = useCallback(async () => {
		if (!selectedOrderId) return;
		try {
			setIsBusy(true);
			await assignOrderToInvoice(invoiceData.id, selectedOrderId);
			onSuccess();
		} catch (error) {
			// The backend refuses an invoice that is pending, a draft, has orders, or has a quota. Its message
			// on invoiceId names the case.
			setServerErrorMessage(getValidationMessage(error, ["invoiceId"]) ?? t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [invoiceData.id, onSuccess, selectedOrderId]);

	const uniqueProjectPhaseIds = Array.from(new Set(allAvailableOrders.map(order => order.projectPhaseId)));

	const OrderContent = allPhasesData
		.filter(phase => uniqueProjectPhaseIds.includes(phase.id))
		.sort(byObjectProperty("phaseNumber", "desc"))
		.map((phase) => {
			const ordersForPhase = allAvailableOrders.filter(order => order.projectPhaseId === phase.id);
			if (ordersForPhase.length === 0) {
				return null;
			}
			return (
				<div key={phase.id}
					 className="mb-4">
					<div className="mb-2 text-sm text-primary-500">{"Phase " + getFullPhaseTitle(phase)}</div>
					<div className="space-y-4">
						{ordersForPhase.map((order) => (
							<button key={order.id}
									className={clsx("w-full rounded-lg border px-4 py-3 text-left hover:bg-primary-500 hover:text-white",
										selectedOrderId === order.id ? "bg-success-600 text-white hover:bg-success-700" : "bg-white text-primary-500",
									)}
									onClick={() => setSelectedOrderId(order.id)}>
								{order.title}
							</button>
						))}
					</div>
				</div>
			);
		});

	return (
		<div className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Bestellung ausweisen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Speichern fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Projekt">
						<div>
							{`${invoiceData.projectNumber} - ${invoiceData.projectTitle}`}
						</div>
					</FormSection>
					<FormSection title="Bestellungen">
						{OrderContent}
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={false}
								   className="mr-2" />
				<Button
					onClick={handleSubmit}
					disabled={!selectedOrderId}>
					Bestellung zuweisen
				</Button>
				<Button theme="white"
						onClick={onCancel}>
					Cancel
				</Button>
			</SidebarFooter>
		</div>);
};

export default AssignProcessedInvoiceToOrderForm;
